import React from "react";
import katex from "katex";

// We'll use the type that ReactMarkdown expects for components
type CodeComponentProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLElement>,
  HTMLElement
> & {inline?: boolean; className?: string};

// This component renders code blocks and processes LaTeX expressions within them
const CustomCodeRenderer = ({
  className,
  children,
  inline,
  ...props
}: CodeComponentProps) => {
  const codeContent = String(children).replace(/\n$/, "");

  // Check if the content contains LaTeX expressions
  const hasLatex = /\$\$(.*?)\$\$/g.test(codeContent);

  // For code blocks with LaTeX, we'll handle them differently
  if (hasLatex && !inline) {
    // Split the content by LaTeX expressions
    const parts = codeContent.split(/(\$\$.*?\$\$)/g);

    // Create an array of elements, alternating between code and LaTeX
    const elements = parts.map((part, index) => {
      // Check if this part is a LaTeX expression
      if (part.startsWith("$$") && part.endsWith("$$")) {
        try {
          // Extract the LaTeX content without the $$ delimiters
          const latex = part.slice(2, -2);
          // Create a div for the LaTeX content
          return (
            <div
              key={index}
              className="latex-formula"
              dangerouslySetInnerHTML={{
                __html: katex.renderToString(latex, {
                  throwOnError: false,
                  displayMode: true,
                }),
              }}
            />
          );
        } catch (error) {
          console.error("Error rendering LaTeX:", error);
          return <div key={index}>{part}</div>;
        }
      } else if (part.trim()) {
        // Regular code content
        return <div key={index}>{part}</div>;
      }
      return null;
    });

    // Filter out null elements
    const filteredElements = elements.filter(Boolean);

    return (
      <code className={`${className || ""} latex-code-block`} {...props}>
        {filteredElements}
      </code>
    );
  }

  // For regular code without LaTeX or inline code, just return the content as is
  // Using CSS classes for styling instead of inline styles

  if (inline) {
    return (
      <code className={`${className || ""} latex-inline-code`} {...props}>
        {codeContent}
      </code>
    );
  }

  return (
    <code className={`${className || ""} latex-code-block`} {...props}>
      {codeContent}
    </code>
  );
};

export default CustomCodeRenderer;
